import {
  useSelector as useReduxSelector,
  useDispatch as useReduxDispatch,
  TypedUseSelectorHook,
} from 'react-redux';
import { Dispatch, combineReducers, Reducer } from 'redux';

import { balancesReducer } from './balances';
import { currentUserReducer } from './currentUser/currentUserReducer';
import { currentUserDataReducer } from './currentUserData';
import { delegatedKeyReducer } from './delegatedKey';
import { exchangeReducer } from './exchange';
import { marketsReducer } from './markets';
import { noticesReducer } from './notices/noticesReducer';
import { ordersReducer } from './orders';
import { regionReducer } from './region/reducer';
import { screenReducer } from './screen/screenReducer';
import { userSettingsReducer } from './userSettings/userSettingsReducer';
import { walletDetailReducer } from './wallet/walletDetailsReducer';
import { walletReducer } from './wallet/walletReducer';
import { walletSectionReducer } from './walletSection/walletSectionReducer';

export const rootReducer = combineReducers({
  // router: routerReducer,

  screen: screenReducer,
  exchange: exchangeReducer,

  balances: balancesReducer,
  wallet: walletReducer,
  walletDetail: walletDetailReducer,
  walletSection: walletSectionReducer,
  markets: marketsReducer,
  orders: ordersReducer,
  currentUser: currentUserReducer,
  currentUserData: currentUserDataReducer,
  userSettings: userSettingsReducer,
  delegatedKey: delegatedKeyReducer,
  /**
   * regional state which holds various state indicating the users current region state.  This may
   * include their restricted region status, current region based on the logged in user, and/or
   * their current region according to their IP address (when logged out).
   */
  region: regionReducer,
  /**
   * Dynamic notices that are rendered by the UI in either the notification position (very top) or the
   * banner position (under the header/menu bar).
   */
  notices: noticesReducer,

  // demo: demoReducer,
  // competition: competitionReducer,
} as const);

type ReducerActions<R extends Reducer<any, any>> = R extends Reducer<
  any,
  infer U
>
  ? U
  : never;

export type ReduxActions = ReducerActions<typeof rootReducer>;

export type ReduxState = ReturnType<typeof rootReducer>;

/**
 * For Initial state setup some state parts of combineReducers
 * are not initialized fully and we have to use nested Partial.
 *
 * Previously (Redux v4) as `PreloadedState<ReduxState>`.
 *
 * Inspired by https://stackoverflow.com/questions/71187691/recursive-partial-with-depth
 */
type PartialWithExtraSecondLayer<T> = {
  [K in keyof T]?: K extends
    | 'markets'
    | 'currentUserData'
    | 'exchange'
    | 'delegatedKey'
    ? Partial<T[K]>
    : T[K];
};

/**
 * Shape of State with some top level reducers as optional,
 * and other combinedReducers as optional (defined list) or required.
 */
export type ReduxStateShape = PartialWithExtraSecondLayer<ReduxState>;

export const useSelector: TypedUseSelectorHook<ReduxState> = useReduxSelector;

/**
 * useDispatch redux hook
 * @note
 *  Variable is not stale so it's preferred to use `staticDispatch`
 *  when re-render is unwanted (eg. in useEffects)
 */
export const useDispatch: () => Dispatch<ReduxActions> = useReduxDispatch;
